import { useEffect, useState } from 'react';
import type { QuotaExceededError, QuotaUsage } from '@open-design/contracts';

export type { QuotaUsage };

export type QuotaUsageFetchFailureKind = 'http' | 'invalid' | 'network';

export type QuotaUsageFetchResult =
  | { ok: true; usage: QuotaUsage }
  | {
      ok: false;
      kind: QuotaUsageFetchFailureKind;
      status: number;
      message: string;
    };

function parseQuotaUsage(body: unknown): QuotaUsage | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  if (
    typeof b.plan !== 'string' ||
    typeof b.role !== 'string' ||
    typeof b.used !== 'number' ||
    typeof b.limit !== 'number' ||
    typeof b.resetsAt !== 'number' ||
    typeof b.periodStart !== 'number' ||
    (b.override !== null && typeof b.override !== 'number')
  ) {
    return null;
  }
  return b as unknown as QuotaUsage;
}

async function readQuotaFailureMessage(resp: Response): Promise<string> {
  try {
    const text = await resp.text();
    if (!text) return resp.statusText || `HTTP ${resp.status}`;
    try {
      const body = JSON.parse(text) as {
        error?: unknown;
        message?: unknown;
      };
      if (typeof body.message === 'string') return body.message;
      if (typeof body.error === 'string') return body.error;
    } catch {
      // Fall through to raw text below.
    }
    return text.slice(0, 240);
  } catch {
    return resp.statusText || `HTTP ${resp.status}`;
  }
}

/**
 * Status-aware fetch of `GET /api/users/me/usage` (Phase C-3 quotas).
 *
 * Surfaces status, network, and invalid-body details for UI that needs to
 * render a visible recovery state. `fetchQuotaUsage()` below preserves the
 * original null-on-failure contract for bootstrap and badge callers.
 */
export async function fetchQuotaUsageResult(): Promise<QuotaUsageFetchResult> {
  try {
    const resp = await fetch('/api/users/me/usage', {
      // Session JWT lives in an http-only cookie; same-origin auto-sends it.
      // Be explicit so cross-origin test environments behave predictably.
      credentials: 'same-origin',
    });
    if (!resp.ok) {
      return {
        ok: false,
        kind: 'http',
        status: resp.status,
        message: await readQuotaFailureMessage(resp),
      };
    }
    const body: unknown = await resp.json();
    const usage = parseQuotaUsage(body);
    if (!usage) {
      return {
        ok: false,
        kind: 'invalid',
        status: resp.status,
        message: 'Invalid usage response',
      };
    }
    return { ok: true, usage };
  } catch (err) {
    return {
      ok: false,
      kind: 'network',
      status: 0,
      message: err instanceof Error ? err.message : 'Network error',
    };
  }
}

/**
 * Best-effort quota fetch for callers that treat failure as "unknown usage."
 * Returns null on network, non-2xx, bad JSON, or shape mismatch so the header
 * badge stays hidden and legacy localStorage flows keep working.
 */
export async function fetchQuotaUsage(): Promise<QuotaUsage | null> {
  const result = await fetchQuotaUsageResult();
  if (!result.ok) {
    return null;
  }
  return result.usage;
}

// Module-level subscription store. The web codebase has no Zustand / Redux
// convention (config and projects state both live as module-level mutables
// or component-local useState), so a tiny set-of-listeners pattern keeps
// useQuota() free of React Context boilerplate. Single global "current
// quota" snapshot, not per-component state.
const listeners = new Set<(v: QuotaUsage | null) => void>();
let current: QuotaUsage | null = null;

/**
 * Push a new value to the store and notify subscribers. App.tsx calls this
 * once at bootstrap with the result of fetchQuotaUsage(); subsequent tasks
 * (8/9/10) call it after run-completion to keep the badge in sync with the
 * server-side counter.
 */
export function setQuota(v: QuotaUsage | null): void {
  current = v;
  for (const l of listeners) l(v);
}

/** Re-fetch from the daemon and update the store. */
export async function refreshQuota(): Promise<void> {
  setQuota(await fetchQuotaUsage());
}

/**
 * React hook — returns the current snapshot, re-rendering on store change.
 * Components that need quota visibility (Task 8 badge, Task 9 settings,
 * Task 10 toast/modal) consume this; nothing else writes to the store.
 */
export function useQuota(): QuotaUsage | null {
  const [v, setV] = useState<QuotaUsage | null>(current);
  useEffect(() => {
    listeners.add(setV);
    // Re-sync to the latest snapshot in case it changed between mount and
    // the effect firing — useState's initializer ran synchronously above,
    // so a setQuota() during that microtask would otherwise be missed.
    setV(current);
    return () => {
      listeners.delete(setV);
    };
  }, []);
  return v;
}

// ---------------------------------------------------------------------------
// Phase C-3 Task 10 — quota-exceeded error event channel.
//
// Mirrors the useQuota() store but for the transient 402 error body. The
// daemon provider catches the 402 response on /api/runs and pushes the
// parsed body via setQuotaErrorEvent(); App.tsx subscribes via
// useQuotaError() to mount <QuotaExceededModal/> as a top-level overlay.
// Same module-level set-of-listeners pattern keeps it Context-free.
// ---------------------------------------------------------------------------
const errorListeners = new Set<(e: QuotaExceededError | null) => void>();
let currentError: QuotaExceededError | null = null;

/**
 * Push a new 402 error (or null to clear) to the store and notify
 * subscribers. Daemon provider calls this with the parsed body when
 * /api/runs returns 402; the modal's onClose handler clears it.
 */
export function setQuotaErrorEvent(e: QuotaExceededError | null): void {
  currentError = e;
  for (const l of errorListeners) l(e);
}

/**
 * React hook — returns [error, dismiss]. The dismiss function clears the
 * stored error (so the modal unmounts). Re-renders on store change.
 */
export function useQuotaError(): [QuotaExceededError | null, () => void] {
  const [v, setV] = useState<QuotaExceededError | null>(currentError);
  useEffect(() => {
    errorListeners.add(setV);
    // Re-sync — same race-defense as useQuota() above.
    setV(currentError);
    return () => {
      errorListeners.delete(setV);
    };
  }, []);
  return [v, dismissQuotaError];
}

function dismissQuotaError(): void {
  setQuotaErrorEvent(null);
}

/**
 * Returns true exactly once per `periodStart` when the user crosses 80%
 * usage — so the parent can render a one-time toast. Pinned in
 * localStorage so reloads inside the same period don't re-warn.
 *
 * Edge cases:
 * - quota null / limit <= 0 → never warns (no period to track / unlimited).
 * - localStorage unavailable (SSR, private browsing) → quietly no-ops; the
 *   user just won't see the warning rather than the app crashing.
 */
export function useEightyPercentToast(quota: QuotaUsage | null): boolean {
  const [shouldShow, setShouldShow] = useState(false);
  const used = quota?.used;
  const limit = quota?.limit;
  const periodStart = quota?.periodStart;
  useEffect(() => {
    if (used == null || limit == null || periodStart == null) return;
    if (limit <= 0) return;
    const ratio = used / limit;
    if (ratio < 0.8) return;
    if (ratio >= 1) return; // 100%+ — let the 402 modal handle it instead.
    try {
      const key = `od.quota.warned.${periodStart}`;
      if (typeof localStorage === 'undefined') return;
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
      setShouldShow(true);
    } catch {
      /* ignore — best-effort */
    }
  }, [used, limit, periodStart]);
  return shouldShow;
}
